import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";

function ownKeys(object, enumerableOnly) {
  var keys = Object.keys(object);

  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);
    if (enumerableOnly) symbols = symbols.filter(function (sym) {
      return Object.getOwnPropertyDescriptor(object, sym).enumerable;
    });
    keys.push.apply(keys, symbols);
  }

  return keys;
}

function _objectSpread(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i] != null ? arguments[i] : {};

    if (i % 2) {
      ownKeys(Object(source), true).forEach(function (key) {
        _defineProperty(target, key, source[key]);
      });
    } else if (Object.getOwnPropertyDescriptors) {
      Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
      ownKeys(Object(source)).forEach(function (key) {
        Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
      });
    }
  }

  return target;
}
/* eslint-disable consistent-return */

/* eslint-disable no-plusplus, no-param-reassign */


import { useEffect, useContext } from 'react';
import { FlipContext } from "./FlipContext";
import { useRegisterFlipChild } from "./useRegisterFlipChild";
import { BORDER_RADIUS, DURATION } from "./flip-constants";
import { easeCurves } from "../../motion/eases";
var matrixRegex = /matrix\((.*), .*, .*, (.*), (.*), (.*)\)/;
/**
 * This needs to be used in the context of a FLIP trigger.
 * Ie., you you to register this child and imperatively trigger
 * the update in that component, which will trigger FLIPS
 * in all registered children (plus inverting flips)
 */

export function useFlip(ref) {
  var _ref = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {},
      _ref$calculateFlip = _ref.calculateFlip,
      calculateFlip = _ref$calculateFlip === void 0 ? flip : _ref$calculateFlip,
      _ref$generateKeyframe = _ref.generateKeyframes,
      generateKeyframes = _ref$generateKeyframe === void 0 ? true : _ref$generateKeyframe,
      _animate = _ref.animate,
      reset = _ref.reset,
      _ref$enabled = _ref.enabled,
      enabled = _ref$enabled === void 0 ? true : _ref$enabled,
      _ref$borderRadius = _ref.borderRadius,
      borderRadius = _ref$borderRadius === void 0 ? BORDER_RADIUS : _ref$borderRadius;

  var _useContext = useContext(FlipContext),
      registerChild = _useContext.registerChild,
      unregisterChild = _useContext.unregisterChild;

  var childRegister = useRegisterFlipChild();
  useEffect(function () {
    var before;
    var after;
    var tx = 0;
    var ty = 0;
    var sy = 0;
    var sx = 0;
    var opacity = null;
    var w = null;
    var h = null;

    if (!enabled) {
      return;
    }

    var fns = {
      // Provide a DOM snapshot of this element prior to layout changes.
      snapshotBefore: function snapshotBefore() {
        // Determine some active transition styles to better
        // enable interruptibe / resumable animations using
        // the web-animations API.
        h = null;
        w = null;

        var _window$getComputedSt = window.getComputedStyle(ref.current),
            transform = _window$getComputedSt.transform,
            computedOpacity = _window$getComputedSt.opacity,
            width = _window$getComputedSt.width,
            height = _window$getComputedSt.height;

        w = width;
        h = height;
        var match = transform.match(matrixRegex);

        if (match && match.length === 5) {
          sx = parseFloat(match[1]);
          sy = parseFloat(match[2]);
          tx = parseFloat(match[3]);
          ty = parseFloat(match[4]);
        }

        opacity = computedOpacity; // Allow the hook consumer to "reset" any active transition
        // styles, to provide accurate before / after measurements.

        if (reset) {
          reset(ref.current);
        }

        before = ref.current.getBoundingClientRect();
        childRegister.registeredChildren.current.forEach(function (child) {
          return child.snapshotBefore && child.snapshotBefore(before);
        });
      },
      // Provide a DOM snapshot of this element after layout changes.
      snapshotAfter: function snapshotAfter() {
        after = ref.current.getBoundingClientRect();
        childRegister.registeredChildren.current.forEach(function (child) {
          return child.snapshotAfter && child.snapshotAfter(after);
        });
      },

      /**  */
      animate: function animate(animation, other) {
        var _ref2 = animation || {},
            _ref2$deltaX = _ref2.deltaX,
            parentDeltaX = _ref2$deltaX === void 0 ? 0 : _ref2$deltaX,
            _ref2$deltaY = _ref2.deltaY,
            parentDeltaY = _ref2$deltaY === void 0 ? 0 : _ref2$deltaY;

        var _calculateFlip = calculateFlip(before, after, other),
            deltaX = _calculateFlip.deltaX,
            deltaY = _calculateFlip.deltaY,
            deltaW = _calculateFlip.deltaW,
            deltaH = _calculateFlip.deltaH;

        var keyframes;

        if (generateKeyframes) {
          var _createKeyFrameAnimat = createKeyFrameAnimation({
            scaleX: !sx || sx === 1 ? deltaW : deltaW - (1 - sx),
            scaleY: !sy || sy === 1 ? deltaH : deltaH - (1 - sy),
            y: deltaY - parentDeltaY + ty,
            x: deltaX - parentDeltaX + tx,
            radius: borderRadius
          }),
              generatedKeyframes = _createKeyFrameAnimat.keyframes,
              invertedKeyframes = _createKeyFrameAnimat.invertedKeyframes,
              invertedKeyframesScaleX = _createKeyFrameAnimat.invertedKeyframesScaleX,
              invertedKeyframesScaleY = _createKeyFrameAnimat.invertedKeyframesScaleY;

          keyframes = generatedKeyframes; // Enabling child scale inversions is only enabled if we've
          // actually generated keyframes. These keyframes are necessary
          // to enable scale inversion using CSS.

          childRegister.registeredChildren.current.forEach(function (child) {
            return child.animate && child.animate({
              deltaX: deltaX,
              deltaY: deltaY,
              deltaW: deltaW,
              deltaH: deltaH,
              parentSnapshotBefore: before,
              parentSnapshotAfter: after,
              keyframes: keyframes,
              invertedKeyframes: invertedKeyframes,
              invertedKeyframesScaleX: invertedKeyframesScaleX,
              invertedKeyframesScaleY: invertedKeyframesScaleY
            });
          });
        }

        _animate(ref, keyframes, {
          opacity: opacity,
          sx: sx,
          sy: sy,
          h: h,
          w: w,
          deltaX: deltaX - parentDeltaX + tx,
          deltaY: deltaY - parentDeltaX + ty,
          deltaH: deltaH,
          deltaW: deltaW,
          before: before,
          after: after
        }, other);
      }
    };
    registerChild(fns);
    return function () {
      unregisterChild(fns);
    };
  }, [enabled, _animate, ref, borderRadius, registerChild, unregisterChild, childRegister, calculateFlip, reset, generateKeyframes]);
  return {
    childRegister: childRegister
  };
}
export function animateFlip(ref, keyframes) {
  var duration = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : DURATION;
  var ease = arguments.length > 3 ? arguments[3] : undefined;
  return ref.current.animate(keyframes, {
    duration: duration,
    ease: ease || easeCurves.primary
  });
}
/**
 * Perform a basic FLIP between two positions. If width or height
 * are 0 for either before or after measurements, don't
 * perform a scale.
 */

export function flip(first, last) {
  var deltaW = 1;
  var deltaH = 1;

  if (first.width !== 0 && last.width !== 0) {
    deltaW = first.width / last.width;
  }

  if (first.height !== 0 && last.height !== 0) {
    deltaH = first.height / last.height;
  }

  return {
    deltaX: first.left - last.left,
    deltaY: first.top - last.top,
    deltaW: deltaW,
    deltaH: deltaH
  };
} // more eases here: https://gist.github.com/gre/1650294

function easeOutCubic(t) {
  return --t * t * t + 1;
}

export function createKeyFrameAnimation(_ref3) {
  var x = _ref3.x,
      y = _ref3.y,
      scaleX = _ref3.scaleX,
      scaleY = _ref3.scaleY,
      _ref3$radius = _ref3.radius,
      radius = _ref3$radius === void 0 ? BORDER_RADIUS : _ref3$radius;
  var keyframes = [];
  var invertedKeyframes = [];
  var invertedKeyframesScaleY = [];
  var invertedKeyframesScaleX = [];

  for (var step = 0; step <= 100; step += 1) {
    // Remap the step value to an eased on.
    var easedStep = easeOutCubic(step / 100); // Calculate the scale of the element.

    var xScale = scaleX + (1 - scaleX) * easedStep;
    var yScale = scaleY + (1 - scaleY) * easedStep; // Calculate the position of the element.

    var xPos = x - x * easedStep;
    var yPos = y - y * easedStep; // And now we invert the contents of the scale

    var invXScale = 1 / xScale;
    var invYScale = 1 / yScale; // Todo (Ben): optimize to only do the necessary transforms

    keyframes.push(_objectSpread({
      transform: getTransform(xPos, yPos, xScale, yScale)
    }, invertBorderRadius(radius, invXScale, invYScale))); // Similarly, if our scales haven't changed we don't need to
    // invert any keyframes and can save our children the effort
    // of inversion.

    invertedKeyframes.push({
      transform: "scale(".concat(invXScale, ", ").concat(invYScale, ")")
    });
    invertedKeyframesScaleY.push({
      transform: "scale(1, ".concat(invYScale, ")")
    });
    invertedKeyframesScaleX.push({
      transform: "scale(".concat(invXScale, ", 1)")
    });
  }

  return {
    keyframes: keyframes,
    invertedKeyframes: invertedKeyframes,
    invertedKeyframesScaleX: invertedKeyframesScaleX,
    invertedKeyframesScaleY: invertedKeyframesScaleY
  };
}

function getTransform(xPos, yPos, xScale, yScale) {
  return "translateX(".concat(xPos, "px) translateY(").concat(yPos, "px) scale(").concat(xScale, ", ").concat(yScale, ")");
}
/**
 * Invert a border radius given inversionX and inversionY
 * of the FLIP.
 */


function invertBorderRadius(radius, invXScale, invYScale) {
  if (!radius) {
    return {};
  }

  var xRadius = invXScale * radius;
  var yRadius = invYScale * radius;
  return {
    borderRadius: "".concat(xRadius, "px / ").concat(yRadius, "px")
  };
}